import type {VariableType} from '@wix/document-services-types'

/**
 * Maps CSS property/variable types to their default variable type name.
 * The key corresponds to the CSS property/variable type
 * and the value is the default variable type to be used for this property
 */
export const DEFAULT_VARIABLES_TYPES: Record<string, VariableType> = {
    // CSS Variable Types
    color: 'ColorVar',
    angle: 'AngleVar',
    length: 'LengthVar',
    number: 'NumVar',
    percentage: 'PercentageVar',
    // Note: string and time do not have corresponding variable types in the manifest

    // CSS Property Types - Colors
    borderRightColor: 'ColorVar',
    borderLeftColor: 'ColorVar',
    borderBottomColor: 'ColorVar',
    borderTopColor: 'ColorVar',
    textDecorationColor: 'ColorVar',
    backgroundColor: 'ColorVar',

    // CSS Property Types - Borders
    borderTopStyle: 'BorderEdgeStyleVar',
    borderBottomStyle: 'BorderEdgeStyleVar',
    borderLeftStyle: 'BorderEdgeStyleVar',
    borderRightStyle: 'BorderEdgeStyleVar',
    borderInlineStartStyle: 'BorderEdgeStyleVar',
    borderInlineEndStyle: 'BorderEdgeStyleVar',

    borderTopWidth: 'BorderEdgeWidthVar',
    borderBottomWidth: 'BorderEdgeWidthVar',
    borderLeftWidth: 'BorderEdgeWidthVar',
    borderRightWidth: 'BorderEdgeWidthVar',
    borderInlineStartWidth: 'BorderEdgeWidthVar',
    borderInlineEndWidth: 'BorderEdgeWidthVar',

    borderWidth: 'BorderWidthVar',
    borderStyle: 'BorderStyleVar',
    borderColor: 'BorderColorVar',
    borderInlineStartColor: 'BorderColorVar',
    borderInlineEndColor: 'BorderColorVar',

    borderRight: 'BorderVar',
    borderLeft: 'BorderVar',
    borderTop: 'BorderVar',
    borderBottom: 'BorderVar',
    border: 'BorderVar',
    borderInlineStart: 'BorderVar',
    borderInlineEnd: 'BorderVar',

    borderTopLeftRadius: 'BorderCornerRadiusVar',
    borderTopRightRadius: 'BorderCornerRadiusVar',
    borderBottomLeftRadius: 'BorderCornerRadiusVar',
    borderBottomRightRadius: 'BorderCornerRadiusVar',
    borderStartStartRadius: 'BorderCornerRadiusVar',
    borderStartEndRadius: 'BorderCornerRadiusVar',
    borderEndStartRadius: 'BorderCornerRadiusVar',
    borderEndEndRadius: 'BorderCornerRadiusVar',

    borderRadius: 'BorderRadiusVar',

    // CSS Property Types - Fonts
    font: 'FontVar',
    fontFamily: 'FontFamilyVar',
    fontSize: 'FontSizeVar',
    fontStretch: 'FontStretchVar',
    fontStyle: 'FontStyleVar',
    fontVariant: 'FontVariantVar',
    fontWeight: 'FontWeightVar',
    lineHeight: 'LineHeightVar',
    letterSpacing: 'LetterSpacingVar',

    // CSS Property Types - Text
    textAlign: 'TextAlignVar',
    textTransform: 'TextTransformVar',
    textShadow: 'TextShadowVar',
    textDecoration: 'TextDecorationVar',
    textDecorationLine: 'TextDecorationLineVar',
    textDecorationStyle: 'TextDecorationStyleVar',
    textDecorationThickness: 'TextDecorationThicknessVar',

    // CSS Property Types - Spacing
    padding: 'PaddingVar',
    paddingTop: 'PaddingEdgeVar',
    paddingBottom: 'PaddingEdgeVar',
    paddingLeft: 'PaddingEdgeVar',
    paddingRight: 'PaddingEdgeVar',
    paddingInlineStart: 'PaddingEdgeVar',
    paddingInlineEnd: 'PaddingEdgeVar',

    margin: 'MarginVar',
    marginTop: 'MarginEdgeVar',
    marginBottom: 'MarginEdgeVar',
    marginLeft: 'MarginEdgeVar',
    marginRight: 'MarginEdgeVar',
    marginInlineStart: 'MarginEdgeVar',
    marginInlineEnd: 'MarginEdgeVar',

    // CSS Property Types - Other
    boxShadow: 'BoxShadowVar',
    opacity: 'OpacityVar',
    background: 'BackgroundVar',
    justifyContent: 'CssJustifyContentVar',
    writingMode: 'WritingModeVar',
    display: 'DisplayVar',
    filter: 'FilterVar',
    backdropFilter: 'FilterVar',
    gap: 'GapVar'
}

const ADDITIONAL_VARIABLE_TYPES: Record<string, VariableType[]> = {
    background: ['ColorVar']
}

/**
 * Maps CSS property/variable types to an array of supported variable types.
 * For now, this contains the same single type as DEFAULT_VARIABLES_TYPES,
 * but will be extended in the future to support multiple variable types per CSS property.
 */
export const SUPPORTED_VARIABLE_TYPES: Record<string, VariableType[]> = Object.fromEntries(
    Object.entries(DEFAULT_VARIABLES_TYPES).map(([key, value]) => {
        if (ADDITIONAL_VARIABLE_TYPES[key]) {
            return [key, [value].concat(ADDITIONAL_VARIABLE_TYPES[key])]
        }
        return [key, [value]]
    })
) as Record<string, VariableType[]>
